import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    Row, Col,
} from '@zillow/zrs-comet';

import {
    Alert,
    Banner,
    Button,
    Spacer,
    TextButton,
} from '@zillow/constellation';

import { RMWContext } from 'app/shared/providers/RMWContext';
import { RoutingSelectors } from 'app/shared/selectors';

import PropertiesActions from 'app/shared/actions/PropertiesActions';
import ApplicationActions from 'app/shared/actions/ApplicationActions';
import ModalActions from 'app/shared/actions/ModalActions';

import { DELIMITER } from 'app/shared/constants/AnalyticsDelimiterConstants';

import {
    GA_EVENTS,
    GA_PREFIX,
} from 'app/shared/constants/AnalyticsInterestedRentersModuleConstants';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import stringHelper from 'app/shared/utils/stringHelper';
import ModalConstants from 'app/shared/constants/ModalConstants';

import AlertWithTitle from 'app/shared/components/AlertWithTitle';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';

import { StyledHeading } from 'app/shared/components/ConnectedInterestedRenters/StyledComponents';

import ApplicationItem from './components/ApplicationItem';
import ApplicationsDisabledCard from './components/ApplicationsDisabledCard';
import ApplicationsSummaryCard from './components/ApplicationsSummaryCard';
import EmptyApplicationsCard from './components/EmptyApplicationsCard';

import {
    StyledButton,
    StyledDivContainer,
    StyledHeadingLink,
    StyledSpinnerCard,
    StyledRowApplicationCardsContainer,
} from './StyledComponents';

const propTypes = {
    updateMinModuleHeight: PropTypes.func,
};

const defaultProps = {
    updateMinModuleHeight: () => {},
};

const {
    ENABLES_APPLICATIONS_CLICK,
    INVITE_TO_APPLY_EMPTY_STATE_CLICK,
    INVITE_TO_APPLY_HAS_APPLICATIONS_CLICK,
    LEARN_MORE_APPLICATIONS_EMPTY_STATE_CLICK,
    VIEW_ALL_APPLICATIONS_CLICK,
    VIEW_ALL_APPLICATIONS_OVERFLOW_TILE_CLICK,
    VIEW_APPLICATION_CLICK,
} = GA_EVENTS;

const { VIEW_INDIVIDUAL_APPLICATION } = GA_PREFIX;

const ConnectedApplications = ({ updateMinModuleHeight }) => {
    const dispatch = useDispatch();
    const { paths } = useContext(RMWContext);

    const listingId = useSelector(state => RoutingSelectors.makeListingId(state));
    const location = useSelector(state => RoutingSelectors.makeLocation(state));

    const [applicationsData, setApplicationsData] = useState({});
    const [showSpinner, setShowSpinner] = useState(true);
    const [showApplicationsEnabledBanner, setShowApplicationsEnabledBanner] = useState(false);
    const [showApplicationsDisabledCard, setShowApplicationsDisabledCard] = useState(false);
    const [showEmptyApplicationsCard, setShowEmptyApplicationsCard] = useState(false);
    const [showApplicationsCards, setShowApplicationsCards] = useState(false);
    const [applicationsUrlPath, setApplicationsUrlPath] = useState('');
    const [viewAllApplicationsText, setViewAllApplicationsText] = useState('View all applications');
    const [showFailureBanner, setShowFailureBanner] = useState(false);
    const [failureBannerText, setFailureBannerText] = useState('');

    const getApplicationsSummaryData = () => {
        dispatch(ApplicationActions.getSummary(listingId))
            .then(({ response }) => {
                const {
                    isApplicationsEnabled,
                    rentalApplicationData: { totalNumberOfApplications },
                    urlPath,
                } = response;

                if (totalNumberOfApplications) {
                    setShowApplicationsCards(true);
                    setViewAllApplicationsText(`View all applications (${totalNumberOfApplications})`);
                } else if (isApplicationsEnabled) {
                    setShowEmptyApplicationsCard(true);
                } else {
                    setShowApplicationsDisabledCard(true);
                }

                setApplicationsData(response);
                setApplicationsUrlPath(urlPath);

                if (showFailureBanner) {
                    setShowFailureBanner(false);
                }
            })
            .catch((err) => {
                setShowFailureBanner(true);

                if (err.code === 'isApplicationsAccessRestricted') {
                    // eslint-disable-next-line no-use-before-define
                    handleAccessRestricted(err);
                    return;
                }

                setFailureBannerText('There was an error fetching your applications. Please try again!');
            })
            .finally(() => {
                setShowSpinner(false);
                updateMinModuleHeight();
            });
    };

    useEffect(() => {
        getApplicationsSummaryData();
    }, [listingId]);

    const displayInviteToApplyModal = () => {
        const { rentalApplicationData: { totalNumberOfApplications } } = applicationsData;
        const hasApplications = totalNumberOfApplications > 0;

        dispatch(ModalActions.showModal({
            modalType: ModalConstants.INVITE_TO_APPLY_MODAL,
            header: 'Invite to apply',
            onClose: getApplicationsSummaryData,
        }));

        if (hasApplications) {
            dispatch(analyticsUtils.track.GAEventV2(INVITE_TO_APPLY_HAS_APPLICATIONS_CLICK));
        } else {
            dispatch(analyticsUtils.track.GAEventV2(INVITE_TO_APPLY_EMPTY_STATE_CLICK));
        }
    };

    const handleAccessRestricted = (err) => {
        const { pathname, search } = location;
        const to = paths.signInPage(`${pathname}${search}`);

        return (
            setFailureBannerText(() => (
                <AlertWithTitle
                    className="AlertWithTitle"
                    appearance="error"
                    title="Access unavailable"
                    body={(
                        <>
                            <p>{err.message}</p>
                            <Spacer marginTop="sm">
                                <Button
                                    as="a"
                                    buttonType="primary"
                                    size="sm"
                                    fluid={true}
                                    href={to}
                                >
                                    Switch Accounts
                                </Button>
                            </Spacer>
                        </>
                    )}
                />
            ))
        );
    };

    const handleEnableApplicationsClick = () => {
        dispatch(PropertiesActions.applicationsAccepted(applicationsData.listingAlias))
            .then(() => {
                setShowApplicationsEnabledBanner(true);
                setShowApplicationsDisabledCard(false);
                setShowEmptyApplicationsCard(true);
                if (showFailureBanner) {
                    setShowFailureBanner(false);
                }
                dispatch(analyticsUtils.track.GAEventV2(ENABLES_APPLICATIONS_CLICK));
            })
            .catch(() => {
                setShowFailureBanner(true);
                setFailureBannerText('There was an error enabling applications. Please try again!');
            });
    };

    const handleLearnMoreClick = () => {
        dispatch(analyticsUtils.track.GAEventV2(LEARN_MORE_APPLICATIONS_EMPTY_STATE_CLICK));
    };

    const handleViewAllApplicationsClick = () => {
        const { rentalApplicationData: { totalNumberOfApplications } } = applicationsData;

        dispatch(analyticsUtils.track.GAEventV2({
            ...VIEW_ALL_APPLICATIONS_CLICK,
            value: totalNumberOfApplications,
        }));
    };

    const onApplicationItemClick = (status) => {
        dispatch(analyticsUtils.track.GAEventV2({
            ...VIEW_APPLICATION_CLICK,
            action: `${VIEW_INDIVIDUAL_APPLICATION}${DELIMITER}${stringHelper.startCaseRemoveSpace(status)}`,
        }));
    };

    const onApplicationSummaryClick = () => {
        const { rentalApplicationData: { totalNumberOfApplications } } = applicationsData;

        dispatch(analyticsUtils.track.GAEventV2({
            ...VIEW_ALL_APPLICATIONS_OVERFLOW_TILE_CLICK,
            value: totalNumberOfApplications,
        }));
    };

    const renderApplicationCards = () => {
        const { rentalApplicationData, remainingSummaryCount } = applicationsData;
        const { remainingCount, remainingCountNew } = remainingSummaryCount;
        const { applicationList, totalNumberOfApplications } = rentalApplicationData;
        const applications = applicationList;
        const showSummaryCard = totalNumberOfApplications > 4;
        const applicationsToRender = showSummaryCard ? applications.slice(0, 3) : applications;

        return (
            <StyledRowApplicationCardsContainer className="ConnectedApplications-ApplicationCards">
                {
                    applicationsToRender.map((application, idx) => {
                        const {
                            statusType,
                            applicants,
                            applicationIsViewed,
                            updateDateUtc,
                            applicationId,
                            conversationId,
                        } = application;

                        return (
                            <Col lg={6} marginBottom="sm" key={idx}>
                                <ApplicationItem
                                    applicationIsViewed={applicationIsViewed}
                                    status={statusType}
                                    applicants={applicants}
                                    updateDateUtc={updateDateUtc}
                                    applicationId={applicationId}
                                    conversationId={conversationId}
                                    onApplicationItemClick={onApplicationItemClick}
                                />
                            </Col>
                        );
                    })
                }

                {
                    showSummaryCard && (
                        <Col lg={6} marginBottom="sm">
                            <ApplicationsSummaryCard
                                urlPath={applicationsUrlPath}
                                remainingCount={remainingCount}
                                remainingCountNew={remainingCountNew}
                                onApplicationSummaryClick={onApplicationSummaryClick}
                            />
                        </Col>
                    )
                }
            </StyledRowApplicationCardsContainer>
        );
    };

    const renderContent = () => {
        if (showApplicationsCards) {
            return (
                <>
                    { renderApplicationCards() }

                    <Row>
                        <Col>
                            <StyledButton
                                buttonType="primary"
                                marginBottom="xs"
                                onClick={displayInviteToApplyModal}
                            >
                                Invite to apply
                            </StyledButton>
                        </Col>
                    </Row>
                </>
            );
        }

        if (showEmptyApplicationsCard) {
            return <EmptyApplicationsCard displayInviteToApplyModal={displayInviteToApplyModal} />;
        }

        if (showApplicationsDisabledCard) {
            return (
                <ApplicationsDisabledCard
                    displayInviteToApplyModal={displayInviteToApplyModal}
                    handleEnableApplicationsClick={handleEnableApplicationsClick}
                    handleLearnMoreClick={handleLearnMoreClick}
                />
            );
        }

        if (showFailureBanner) {
            // renders the AlertWithTitle component
            if (typeof failureBannerText === 'object') {
                return failureBannerText;
            }

            return (
                <Alert
                    appearance="error"
                    body={failureBannerText}
                />
            );
        }
    };

    return (
        <StyledDivContainer className="ConnectedApplications">
            <StyledHeading level={3} paddingBottom="sm">
                Applications

                {
                    showApplicationsEnabledBanner && (
                        <Banner
                            appearance="success"
                            body="Applications have been enabled on your listing"
                        />
                    )
                }
                {
                    showApplicationsCards && (
                        <StyledHeadingLink>
                            <TextButton
                                as="a"
                                fontType="body"
                                escape={true}
                                href={applicationsUrlPath}
                                type="secondary"
                                onClick={handleViewAllApplicationsClick}
                            >
                                { viewAllApplicationsText }
                            </TextButton>
                        </StyledHeadingLink>
                    )
                }
            </StyledHeading>

            {
                showSpinner ? (
                    <StyledSpinnerCard cardType="secondary">
                        <SpinnerContainer />
                    </StyledSpinnerCard>
                ) : renderContent()
            }
        </StyledDivContainer>
    );
};

ConnectedApplications.propTypes = propTypes;
ConnectedApplications.defaultProps = defaultProps;

export default ConnectedApplications;
